import { useLayoutEffect, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ESCAPE_PRIORITY, useEscapeAction } from './escapeArbiter';
import { useTranslation } from 'react-i18next';

interface PaperContextMenuProps {
  readonly x: number;
  readonly y: number;
  readonly canPaste: boolean;
  readonly onPaste: () => void;
  readonly onClose: (restoreFocus?: boolean) => void;
}

/** A portal keeps the menu out of the paper's clipped/zoomed drawing layers. */
export function PaperContextMenu({ x, y, canPaste, onPaste, onClose }: PaperContextMenuProps) {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRef = useRef<HTMLButtonElement>(null);
  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;
    const rect = menu.getBoundingClientRect();
    menu.style.left = `${Math.max(8, Math.min(x, window.innerWidth - rect.width - 8))}px`;
    menu.style.top = `${Math.max(8, Math.min(y, window.innerHeight - rect.height - 8))}px`;
    itemRef.current?.focus({ preventScroll: true });
  }, [x, y]);
  useEffect(() => {
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !menuRef.current?.contains(event.target)) onClose(false);
    };
    const dismiss = () => onClose(false);
    window.addEventListener('pointerdown', outside, true);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    window.addEventListener('blur', dismiss);
    return () => {
      window.removeEventListener('pointerdown', outside, true);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('blur', dismiss);
    };
  }, [onClose]);
  // #762 -- an open menu closes on Escape before the selection would clear (escapeArbiter).
  useEscapeAction(ESCAPE_PRIORITY.overlay, () => {
    onClose();
    return true;
  });
  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      tabIndex={-1}
      aria-label={t('paperContextMenu.label')}
      className="paper-context-menu"
      style={{ left: x, top: y }}
      onPointerDown={(event) => event.stopPropagation()}
      onPointerMove={(event) => event.stopPropagation()}
      onPointerUp={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      onContextMenu={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onWheel={(event) => {
        event.stopPropagation();
        onClose();
      }}
      onKeyDown={(event) => {
        // Editor shortcuts must not run behind this menu; Escape goes on to the arbiter (below).
        if (event.key !== 'Escape') event.stopPropagation();
        if (event.key === 'Tab') onClose();
        else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          itemRef.current?.focus();
        }
      }}
    >
      <button
        ref={itemRef}
        type="button"
        role="menuitem"
        tabIndex={-1}
        aria-disabled={!canPaste}
        onClick={() => {
          if (canPaste) {
            onPaste();
            onClose();
          }
        }}
      >
        {t('paperContextMenu.pasteHere')}
      </button>
    </div>,
    document.body
  );
}
